import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { t, tn } from '../i18n/index.ts';
import { Icon } from './Icon.tsx';
import { Button, IconButton } from './ui.tsx';

/**
 * The "View" button of a DataTable: which columns to show, saved views (a name for a set of columns and a sort,
 * e.g. "Unpaid, by amount"), and the row height of every table. All of it is kept per person on this device and
 * only changes how the table looks; the records and what the person may see stay the same.
 */

export type Sort = { col: number; dir: 'asc' | 'desc' } | null;
export type Density = 'comfortable' | 'compact';
export interface SavedView {
  name: string;
  hidden: number[];
  sort: Sort;
}
export interface TableView {
  hidden: number[];
  sort: Sort;
  views: SavedView[];
}

export const MAX_VIEWS = 12;
const same = (a: number[], b: number[]) => a.length === b.length && [...a].sort().every((x, i) => x === [...b].sort()[i]);
const sameSort = (a: Sort, b: Sort) => (a === null || b === null ? a === b : a.col === b.col && a.dir === b.dir);

/** The saved view the table shows right now, if any. */
export function currentView(v: TableView): SavedView | undefined {
  return v.views.find((x) => same(x.hidden, v.hidden) && sameSort(x.sort, v.sort));
}

/** Saves the table's columns and sort under a name; a view with the same name (any case) is replaced. */
export function saveView(v: TableView, name: string): TableView {
  const n = name.trim().slice(0, 40);
  if (!n) return v;
  const others = v.views.filter((x) => x.name.toLowerCase() !== n.toLowerCase());
  return {
    ...v,
    views: [...others, { name: n, hidden: [...v.hidden], sort: v.sort }].slice(-MAX_VIEWS),
  };
}

export function TableViewPanel({
  head,
  view,
  onView,
  density,
  onDensity,
  sortable,
}: {
  head: string[];
  view: TableView;
  onView: (v: TableView) => void;
  density: Density;
  onDensity: (d: Density) => void;
  sortable: boolean;
}) {
  const [open, setOpen] = useState(false);
  // The panel is placed against the window, so a table that scrolls inside its card never cuts it off.
  const [at, setAt] = useState<{
    top: number | 'auto';
    bottom?: number;
    right: number;
    maxHeight: number;
  } | null>(null);
  const [name, setName] = useState('');
  const box = useRef<HTMLDivElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const id = useId();
  useEffect(() => {
    if (!open) return;
    const close = (e: Event) => {
      if (e instanceof KeyboardEvent) {
        if (e.key !== 'Escape') return;
        trigger.current?.focus();
      } else if (box.current?.contains(e.target as Node) || panel.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    const place = () => {
      const r = trigger.current?.getBoundingClientRect();
      if (!r) return;
      const below = window.innerHeight - r.bottom - 16;
      const right = Math.max(8, window.innerWidth - r.right);
      // opens downwards (scrolling inside when needed); upwards only when the button sits low on the screen
      setAt(
        below >= 320 || below >= r.top - 16
          ? { top: r.bottom + 6, right, maxHeight: below }
          : {
              top: 'auto',
              bottom: window.innerHeight - r.top + 6,
              right,
              maxHeight: r.top - 16,
            },
      );
    };
    place();
    document.addEventListener('pointerdown', close);
    document.addEventListener('keydown', close);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      setAt(null);
      document.removeEventListener('pointerdown', close);
      document.removeEventListener('keydown', close);
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open]);
  // Once placed (and so visible), the panel itself takes the focus; on a phone, focusing the name field would open
  // the keyboard over it.
  const placed = !!at;
  useEffect(() => {
    if (open && placed) panel.current?.focus({ preventScroll: true });
  }, [open, placed]);

  const hidden = view.hidden.filter((i) => i > 0 && i < head.length && head[i]);
  const current = currentView({ ...view, hidden });
  const choosable = head.map((h, i) => ({ h, i })).filter((c) => c.i > 0 && c.h);
  const toggle = (i: number) =>
    onView({
      ...view,
      hidden: hidden.includes(i) ? hidden.filter((x) => x !== i) : [...hidden, i],
    });
  const save = () => {
    onView(saveView({ ...view, hidden }, name));
    setName('');
  };
  const changed = hidden.length > 0 || view.sort !== null;
  const status = current ? current.name : hidden.length ? tn('tv.hidden_count', hidden.length) : null;

  return (
    <div className="pop tv" ref={box}>
      <button
        ref={trigger}
        type="button"
        className={`btn btn-secondary btn-sm tv-trigger${changed ? ' on' : ''}`}
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen((o) => !o)}
      >
        <Icon name="sliders" size={16} />
        <span>{t('tv.view')}</span>
        {status ? <span className="tv-status">{status}</span> : null}
      </button>
      {open
        ? createPortal(
            <div
              ref={panel}
              className="pop-menu tv-panel"
              id={id}
              role="dialog"
              aria-label={t('tv.title')}
              tabIndex={-1}
              style={{ ...at, position: 'fixed', visibility: at ? undefined : 'hidden' }}
            >
              <section className="tv-sec" aria-labelledby={`${id}-views`}>
                <h3 className="tv-h" id={`${id}-views`}>
                  {t('tv.saved')}
                </h3>
                {view.views.length ? (
                  <ul className="tv-views">
                    {view.views.map((v) => (
                      <li key={v.name} className={v === current ? 'on' : undefined}>
                        <button
                          type="button"
                          className="tv-view"
                          aria-current={v === current || undefined}
                          onClick={() =>
                            onView({
                              ...view,
                              hidden: [...v.hidden],
                              sort: v.sort,
                            })
                          }
                        >
                          <Icon name={v === current ? 'check' : 'bookmark'} size={16} />
                          <span>{v.name}</span>
                        </button>
                        <IconButton
                          icon="x"
                          label={t('tv.delete_view', { name: v.name })}
                          className="tv-del"
                          onClick={() =>
                            onView({
                              ...view,
                              views: view.views.filter((x) => x !== v),
                            })
                          }
                        />
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="tv-empty">{t('tv.none')}</p>
                )}
                <form
                  className="tv-save"
                  onSubmit={(e) => {
                    e.preventDefault();
                    save();
                  }}
                >
                  <input
                    className="input"
                    value={name}
                    maxLength={40}
                    placeholder={t('tv.name_placeholder')}
                    aria-label={t('tv.name')}
                    onChange={(e) => setName(e.target.value)}
                  />
                  <Button type="submit" size="sm" disabled={!name.trim()}>
                    {t('tv.save')}
                  </Button>
                </form>
              </section>

              {choosable.length ? (
                <fieldset className="tv-sec">
                  <legend className="tv-h">{t('tv.columns')}</legend>
                  <label className="tv-check">
                    <input type="checkbox" checked disabled />
                    <span>{head[0]}</span>
                  </label>
                  {choosable.map(({ h, i }) => (
                    <label key={i} className="tv-check">
                      <input type="checkbox" checked={!hidden.includes(i)} onChange={() => toggle(i)} />
                      <span>{h}</span>
                    </label>
                  ))}
                </fieldset>
              ) : null}

              <fieldset className="tv-sec">
                <legend className="tv-h">{t('tv.density')}</legend>
                {(['comfortable', 'compact'] as const).map((d) => (
                  <label key={d} className="tv-check">
                    <input type="radio" name={`${id}-density`} checked={density === d} onChange={() => onDensity(d)} />
                    <span>{t(d === 'compact' ? 'tv.compact' : 'tv.comfortable')}</span>
                  </label>
                ))}
                <p className="tv-note">{t('tv.density_note')}</p>
              </fieldset>

              <div className="tv-foot">
                {sortable && view.sort ? <span className="tv-note">{t('tv.sort_kept')}</span> : <span />}
                <Button variant="ghost" size="sm" icon="undo" disabled={!changed} onClick={() => onView({ ...view, hidden: [], sort: null })}>
                  {t('tv.reset')}
                </Button>
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
